import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  Tab,
  TabList,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useContext, useEffect, useState } from 'react';

import General from './General';
import CustomFields from './CustomFields';
import TemplateDefaults from './TemplateDefaults';
import { TDocument, TDraft, TVersion } from '../../../types';
import { TFile, TFileVersion } from '@modules/files';
import AdvancedActions from './AdvancedActions';
import { getVersionId } from '@modules/items';
import { TPermissions } from '@modules/access';
import { SignInContext } from '@modules/users';
import { TERMINOLOGY } from '@shared/i18n';

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

export enum EDocumentConfigTab {
  GENERAL = 'GENERAL',
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
  TEMPLATE_DEFAULTS = 'TEMPLATE_DEFAULTS',
  ADVANCED_ACTIONS = 'ADVANCED_ACTIONS',
}

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  item: TDocument | TFile;
  isDoc: boolean;
  version?: TVersion | TDraft | TFileVersion;
  permissions: TPermissions;
  initialTab?: EDocumentConfigTab;
  openPeriodicReviewDialog?: () => void;
};

const ItemConfigDialog = ({
  isOpen,
  setIsOpen,
  item,
  isDoc,
  version,
  permissions,
  initialTab,
  openPeriodicReviewDialog,
}: TProps) => {
  const defaultTab = isDoc ? EDocumentConfigTab.GENERAL : EDocumentConfigTab.CUSTOM_FIELDS;
  const [selectedTab, setSelectedTab] = useState<EDocumentConfigTab>(defaultTab);
  const classes = useStyles();
  const { organization } = useContext(SignInContext);
  const showTemplateDefaults =
    isDoc && !!organization.featureSettings.templates?.enabled && (item as TDocument).isTemplate;

  useEffect(() => {
    if (isOpen) setSelectedTab(initialTab ?? defaultTab);
  }, [isOpen, initialTab, defaultTab]);

  if (!version) return null;

  const versionId = getVersionId(version);

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Settings for {versionId === 'draft' ? versionId : `v${versionId}`}
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            <TabList
              size="medium"
              selectedValue={selectedTab}
              onTabSelect={(_, data) => setSelectedTab(data.value as EDocumentConfigTab)}
              className="border-b border-gray-200"
            >
              {isDoc && <Tab value={EDocumentConfigTab.GENERAL}>General</Tab>}
              <Tab value={EDocumentConfigTab.CUSTOM_FIELDS}>Custom fields</Tab>
              {showTemplateDefaults && (
                <Tab value={EDocumentConfigTab.TEMPLATE_DEFAULTS}>{TERMINOLOGY.template.upper} defaults</Tab>
              )}
              <Tab value={EDocumentConfigTab.ADVANCED_ACTIONS}>Advanced actions</Tab>
            </TabList>

            {selectedTab === EDocumentConfigTab.GENERAL ? (
              <General doc={item as TDocument} version={version as TVersion | TDraft} versionId={versionId} />
            ) : selectedTab === EDocumentConfigTab.CUSTOM_FIELDS ? (
              <CustomFields item={item} isDoc={isDoc} versionId={versionId} version={version} />
            ) : selectedTab === EDocumentConfigTab.TEMPLATE_DEFAULTS ? (
              <TemplateDefaults doc={item as TDocument} versionId={versionId} version={version as TVersion | TDraft} />
            ) : selectedTab === EDocumentConfigTab.ADVANCED_ACTIONS ? (
              <AdvancedActions
                item={item}
                isDoc={isDoc}
                versionId={versionId}
                version={version}
                permissions={permissions}
                closeSettingsDialog={() => setIsOpen(false)}
                openPeriodicReviewDialog={openPeriodicReviewDialog}
              />
            ) : null}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ItemConfigDialog;
